import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha, Theme } from "@mui/material/styles";
import {
  Check,
  ChevronRight,
  FlightTakeoff,
  HowToReg,
  AssignmentTurnedIn,
  AirplaneTicket,
  WorkOutline,
  VerifiedUser,
  PersonAdd,
  LocalHospital,
  MenuBook,
} from "@mui/icons-material";
import { format } from "date-fns";
import { useMemo } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Employee,
  EmployeeStatus,
  EmployeeStatusEventType,
  EmployeeStatusHistoryEntry,
  MedicalStatus,
} from "../types/processTypes";
import { getEmployeeStatusRemark } from "../utils/statusDate";

type Props = {
  employee?: Employee | null;
  loading?: boolean;
};

type TimelineStep = {
  id: string;
  status?: EmployeeStatus;
  label: string;
  reached: boolean;
  current: boolean;
  date?: Date | string;
  by?: string;
  remark?: string;
  icon: ReactNode;
};

type TimelineItem =
  | { kind: "step"; step: TimelineStep }
  | { kind: "stepWithPrep"; step: TimelineStep; prepSteps: TimelineStep[] };

type VerticalRow =
  | { type: "main"; step: TimelineStep; isLast: boolean }
  | { type: "prep"; step: TimelineStep; showConnector: boolean };

const PIPELINE_STAGES: EmployeeStatus[] = [
  EmployeeStatus.selected,
  EmployeeStatus.contracted,
  EmployeeStatus.visa_issued,
  EmployeeStatus.labour_issued,
  EmployeeStatus.flight_booked,
  EmployeeStatus.departed,
];

const TERMINAL_STATUSES = new Set<EmployeeStatus>([
  EmployeeStatus.discontinued,
  EmployeeStatus.stopped,
  EmployeeStatus.lost,
  EmployeeStatus.returned,
  EmployeeStatus.has_complain,
  EmployeeStatus.contract_completed,
  EmployeeStatus.other,
]);

function formatDate(value?: Date | string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return format(date, "dd MMM yyyy");
}

function formatChangedBy(email?: string) {
  if (!email) return "";
  const local = email.split("@")[0] ?? email;
  return local.replace(/[._-]+/g, " ").trim();
}

function stageIcon(status: EmployeeStatus) {
  switch (status) {
    case EmployeeStatus.selected:
      return <HowToReg sx={{ fontSize: 18 }} />;
    case EmployeeStatus.contracted:
      return <AssignmentTurnedIn sx={{ fontSize: 18 }} />;
    case EmployeeStatus.visa_issued:
      return <VerifiedUser sx={{ fontSize: 18 }} />;
    case EmployeeStatus.labour_issued:
      return <WorkOutline sx={{ fontSize: 18 }} />;
    case EmployeeStatus.flight_booked:
      return <AirplaneTicket sx={{ fontSize: 18 }} />;
    case EmployeeStatus.departed:
      return <FlightTakeoff sx={{ fontSize: 18 }} />;
    default:
      return <Check sx={{ fontSize: 18 }} />;
  }
}

function findFirstReach(
  history: EmployeeStatusHistoryEntry[],
  status: EmployeeStatus,
) {
  return history.find(
    (entry) =>
      entry.status === status &&
      (entry.eventType === EmployeeStatusEventType.status_change ||
        entry.eventType === EmployeeStatusEventType.contracted),
  );
}

function getStageDate(employee: Employee, status: EmployeeStatus) {
  const history = employee.statusHistory ?? [];
  const entry = findFirstReach(history, status);
  if (entry?.effectiveDate) return entry.effectiveDate;

  const milestones = employee.statusDates;
  if (status === EmployeeStatus.selected && milestones?.selected) {
    return milestones.selected;
  }
  if (status === EmployeeStatus.contracted && milestones?.contracted) {
    return milestones.contracted;
  }
  if (status === EmployeeStatus.departed && milestones?.departed) {
    return milestones.departed;
  }
  if (status === EmployeeStatus.visa_issued && employee.visaIssuedDate) {
    return employee.visaIssuedDate;
  }
  if (status === EmployeeStatus.labour_issued && employee.lmisIssuedDate) {
    return employee.lmisIssuedDate;
  }
  if (
    status === EmployeeStatus.flight_booked &&
    employee.flight?.flightDateTime
  ) {
    return employee.flight.flightDateTime;
  }
  return undefined;
}

function getStageBy(employee: Employee, status: EmployeeStatus) {
  const entry = findFirstReach(employee.statusHistory ?? [], status);
  return entry?.changedByEmail;
}

function getStageRemark(employee: Employee, status: EmployeeStatus) {
  const history = employee.statusHistory ?? [];
  const entry = findFirstReach(history, status);
  if (entry?.remark?.trim()) {
    return entry.remark.trim();
  }
  const latest = [...history]
    .reverse()
    .find((item) => item.status === status && item.remark?.trim());
  return latest?.remark?.trim();
}

function pipelineIndex(status: EmployeeStatus) {
  return PIPELINE_STAGES.indexOf(status);
}

function isStageReached(employee: Employee, status: EmployeeStatus) {
  const current = employee.status ?? EmployeeStatus.new;
  if (current === status) return true;

  const stageIdx = pipelineIndex(status);
  const currentIdx = pipelineIndex(current);
  if (stageIdx >= 0 && currentIdx >= 0) {
    return currentIdx > stageIdx;
  }

  if (status === EmployeeStatus.selected) {
    return Boolean(employee.statusDates?.selected) || currentIdx >= 0;
  }
  if (status === EmployeeStatus.contracted) {
    return (
      Boolean(employee.statusDates?.contracted) || Boolean(employee.cocCompleted)
    );
  }
  if (status === EmployeeStatus.visa_issued) return Boolean(employee.visaIssued);
  if (status === EmployeeStatus.labour_issued) {
    return Boolean(employee.labourIssued);
  }
  if (status === EmployeeStatus.flight_booked) {
    return Boolean(employee.ticketBooked);
  }
  if (status === EmployeeStatus.departed) {
    return Boolean(employee.flight?.departed) || current === EmployeeStatus.departed;
  }

  return Boolean(getStageDate(employee, status));
}

function getCreatedBy(employee: Employee) {
  const history = employee.statusHistory ?? [];
  const entry =
    findFirstReach(history, EmployeeStatus.new) ??
    findFirstReach(history, EmployeeStatus.pending) ??
    history[0];
  return entry?.changedByEmail;
}

function findLatestPipelineUpdate(
  history: EmployeeStatusHistoryEntry[],
  predicate: (metadata?: EmployeeStatusHistoryEntry["metadata"]) => boolean,
) {
  return [...history]
    .reverse()
    .find(
      (entry) =>
        entry.eventType === EmployeeStatusEventType.pipeline_update &&
        predicate(entry.metadata),
    );
}

function getMedicalFitDate(employee: Employee) {
  if (employee.medicalFitDate) return employee.medicalFitDate;

  const history = employee.statusHistory ?? [];
  const entries = history.filter(
    (entry) =>
      entry.eventType === EmployeeStatusEventType.pipeline_update &&
      entry.metadata?.medicalStatus === MedicalStatus.fit,
  );
  const earliest = entries.reduce<EmployeeStatusHistoryEntry | undefined>(
    (best, entry) => {
      if (!entry.changedAt) return best;
      if (!best?.changedAt) return entry;
      return new Date(entry.changedAt).getTime() < new Date(best.changedAt).getTime()
        ? entry
        : best;
    },
    undefined,
  );
  if (earliest?.changedAt) return earliest.changedAt;

  return employee.medicalBookedDate ?? employee.medicalExpiryDate;
}

function getCocCompletedDate(employee: Employee) {
  if (employee.cocCompletedDate) return employee.cocCompletedDate;

  const history = employee.statusHistory ?? [];
  const entries = history.filter(
    (entry) =>
      entry.eventType === EmployeeStatusEventType.pipeline_update &&
      entry.metadata?.cocCompleted === true,
  );
  const earliest = entries.reduce<EmployeeStatusHistoryEntry | undefined>(
    (best, entry) => {
      if (!entry.changedAt) return best;
      if (!best?.changedAt) return entry;
      return new Date(entry.changedAt).getTime() < new Date(best.changedAt).getTime()
        ? entry
        : best;
    },
    undefined,
  );
  if (earliest?.changedAt) return earliest.changedAt;

  return employee.statusDates?.contracted;
}

function getMedicalBy(employee: Employee) {
  return findLatestPipelineUpdate(
    employee.statusHistory ?? [],
    (metadata) => metadata?.medicalStatus === MedicalStatus.fit,
  )?.changedByEmail;
}

function getCocBy(employee: Employee) {
  return findLatestPipelineUpdate(
    employee.statusHistory ?? [],
    (metadata) => metadata?.cocCompleted === true,
  )?.changedByEmail;
}

function buildMedicalStep(employee: Employee, label: string): TimelineStep {
  return {
    id: "medical",
    label,
    reached: employee.medicalStatus === MedicalStatus.fit,
    current: false,
    date: getMedicalFitDate(employee),
    by: getMedicalBy(employee),
    icon: <LocalHospital sx={{ fontSize: 16 }} />,
  };
}

function buildCocStep(employee: Employee, label: string): TimelineStep {
  return {
    id: "coc",
    label,
    reached: Boolean(employee.cocCompleted),
    current: false,
    date: getCocCompletedDate(employee),
    by: getCocBy(employee),
    icon: <MenuBook sx={{ fontSize: 16 }} />,
  };
}

function flattenStepsForProgress(items: TimelineItem[]): TimelineStep[] {
  return items.flatMap((item) =>
    item.kind === "stepWithPrep"
      ? [item.step, ...item.prepSteps]
      : [item.step],
  );
}

function flattenVerticalRows(items: TimelineItem[]): VerticalRow[] {
  const rows: VerticalRow[] = [];

  items.forEach((item, index) => {
    const isLastItem = index === items.length - 1;

    if (item.kind === "stepWithPrep") {
      rows.push({
        type: "main",
        step: item.step,
        isLast: false,
      });
      item.prepSteps.forEach((prepStep, prepIndex) => {
        rows.push({
          type: "prep",
          step: prepStep,
          showConnector:
            prepIndex < item.prepSteps.length - 1 || !isLastItem,
        });
      });
      return;
    }

    rows.push({
      type: "main",
      step: item.step,
      isLast: isLastItem,
    });
  });

  return rows;
}

function buildCreatedStep(
  employee: Employee,
  label: string,
): TimelineStep {
  const current = employee.status ?? EmployeeStatus.new;
  const isCurrent =
    current === EmployeeStatus.new || current === EmployeeStatus.pending;

  return {
    id: "created",
    label,
    reached: Boolean(employee.createdAt),
    current: isCurrent,
    date: employee.createdAt,
    by: getCreatedBy(employee),
    remark:
      getStageRemark(employee, EmployeeStatus.new) ??
      getStageRemark(employee, EmployeeStatus.pending),
    icon: <PersonAdd sx={{ fontSize: 18 }} />,
  };
}

function buildTimelineItems(
  employee: Employee,
  labelFor: (status: EmployeeStatus) => string,
  createdLabel: string,
  medicalLabel: string,
  cocLabel: string,
): TimelineItem[] {
  const current = employee.status ?? EmployeeStatus.new;

  const pipelineItems: TimelineItem[] = PIPELINE_STAGES.map((status) => {
    const step: TimelineStep = {
      id: status,
      status,
      label: labelFor(status),
      reached: isStageReached(employee, status),
      current: current === status,
      date: getStageDate(employee, status),
      by: getStageBy(employee, status),
      remark: getStageRemark(employee, status),
      icon: stageIcon(status),
    };

    if (status === EmployeeStatus.contracted) {
      return {
        kind: "stepWithPrep" as const,
        step,
        prepSteps: [
          buildMedicalStep(employee, medicalLabel),
          buildCocStep(employee, cocLabel),
        ],
      };
    }

    return { kind: "step" as const, step };
  });

  return [
    { kind: "step", step: buildCreatedStep(employee, createdLabel) },
    ...pipelineItems,
  ];
}

function buildTerminalStep(
  employee: Employee,
  labelFor: (status: EmployeeStatus) => string,
): TimelineStep | null {
  const current = employee.status ?? EmployeeStatus.new;
  if (!TERMINAL_STATUSES.has(current)) return null;

  const history = employee.statusHistory ?? [];
  const entry =
    [...history]
      .reverse()
      .find(
        (item) =>
          item.status === current &&
          item.eventType === EmployeeStatusEventType.status_change,
      ) ?? history[history.length - 1];

  return {
    id: current,
    status: current,
    label: labelFor(current),
    reached: true,
    current: true,
    date:
      entry?.effectiveDate ??
      employee.statusDates?.[current as keyof typeof employee.statusDates] ??
      employee.currentStatusDate,
    by: entry?.changedByEmail,
    remark:
      entry?.remark?.trim() || getEmployeeStatusRemark(employee)?.trim(),
    icon: stageIcon(current),
  };
}

function stepAccentColor(step: TimelineStep, theme: Theme) {
  if (step.current) return theme.palette.primary.main;
  if (step.reached) return theme.palette.success.main;
  return theme.palette.grey[400];
}

function StepRemark({
  remark,
  align = "center",
}: {
  remark?: string;
  align?: "center" | "left";
}) {
  const { t } = useTranslation();
  if (!remark) return null;

  return (
    <Typography
      variant="caption"
      align={align}
      color="text.secondary"
      sx={{
        mt: 0.5,
        display: "block",
        fontSize: "0.68rem",
        lineHeight: 1.35,
        px: align === "center" ? 0.5 : 0,
        fontStyle: "italic",
      }}
    >
      {t("remark")}: {remark}
    </Typography>
  );
}

function TimelineNode({
  step,
  vertical,
}: {
  step: TimelineStep;
  vertical: boolean;
}) {
  const theme = useTheme();
  const activeColor = stepAccentColor(step, theme);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: vertical ? "100%" : 112,
        maxWidth: vertical ? "100%" : 128,
        px: vertical ? 0 : 0.5,
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: step.reached ? "#fff" : theme.palette.text.disabled,
          bgcolor: step.reached ? activeColor : alpha(theme.palette.grey[300], 0.35),
          border: `2px solid ${activeColor}`,
          boxShadow: step.current
            ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.16)}`
            : "none",
          transition: "all 0.2s ease",
        }}
      >
        {step.reached ? step.icon : step.status ? stageIcon(step.status) : step.icon}
      </Box>
      <Typography
        variant="caption"
        align="center"
        sx={{
          mt: 1.25,
          fontWeight: step.current ? 700 : 500,
          color: step.reached ? "text.primary" : "text.disabled",
          lineHeight: 1.3,
          px: 0.5,
        }}
      >
        {step.label}
      </Typography>
      {step.reached && step.date && (
        <Typography
          variant="caption"
          align="center"
          color="text.secondary"
          sx={{ mt: 0.5, fontSize: "0.72rem", fontWeight: 500 }}
        >
          {formatDate(step.date)}
        </Typography>
      )}
      {step.reached && step.by && (
        <Typography
          variant="caption"
          align="center"
          color="text.secondary"
          sx={{ mt: 0.25, fontSize: "0.68rem", fontStyle: "italic" }}
        >
          {formatChangedBy(step.by)}
        </Typography>
      )}
      {step.reached && <StepRemark remark={step.remark} />}
    </Box>
  );
}

function PrepTimelineNode({ step }: { step: TimelineStep }) {
  const theme = useTheme();
  const activeColor = stepAccentColor(step, theme);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: 112,
        maxWidth: 128,
        px: 0.5,
        mt: 0.75,
      }}
    >
      <Box
        sx={{
          width: 2,
          height: 10,
          bgcolor: step.reached
            ? alpha(theme.palette.success.main, 0.45)
            : alpha(theme.palette.grey[400], 0.35),
          borderRadius: 1,
          mb: 0.5,
        }}
      />
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: step.reached ? "#fff" : theme.palette.text.disabled,
          bgcolor: step.reached ? activeColor : alpha(theme.palette.grey[300], 0.35),
          border: `2px solid ${activeColor}`,
        }}
      >
        {step.icon}
      </Box>
      <Typography
        variant="caption"
        align="center"
        sx={{
          mt: 0.75,
          fontWeight: 500,
          color: step.reached ? "text.primary" : "text.disabled",
          lineHeight: 1.3,
          px: 0.5,
          fontSize: "0.68rem",
        }}
      >
        {step.label}
      </Typography>
      {step.reached && step.date && (
        <Typography
          variant="caption"
          align="center"
          color="text.secondary"
          sx={{ mt: 0.35, fontSize: "0.68rem", fontWeight: 500 }}
        >
          {formatDate(step.date)}
        </Typography>
      )}
      {step.reached && step.by && (
        <Typography
          variant="caption"
          align="center"
          color="text.secondary"
          sx={{ mt: 0.25, fontSize: "0.64rem", fontStyle: "italic" }}
        >
          {formatChangedBy(step.by)}
        </Typography>
      )}
    </Box>
  );
}

function VerticalTimelineStep({
  step,
  isLast,
}: {
  step: TimelineStep;
  isLast: boolean;
}) {
  const theme = useTheme();
  const activeColor = stepAccentColor(step, theme);

  return (
    <Stack direction="row" spacing={2} sx={{ width: "100%", minHeight: 72 }}>
      <Stack alignItems="center" sx={{ width: 48, flexShrink: 0 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: step.reached ? "#fff" : theme.palette.text.disabled,
            bgcolor: step.reached ? activeColor : alpha(theme.palette.grey[300], 0.35),
            border: `2px solid ${activeColor}`,
            boxShadow: step.current
              ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.16)}`
              : "none",
          }}
        >
          {step.reached ? step.icon : step.status ? stageIcon(step.status) : step.icon}
        </Box>
        {!isLast && (
          <Box
            sx={{
              flex: 1,
              width: 2,
              minHeight: 16,
              mt: 0.75,
              bgcolor: step.reached
                ? alpha(theme.palette.success.main, 0.45)
                : alpha(theme.palette.grey[400], 0.35),
              borderRadius: 1,
            }}
          />
        )}
      </Stack>
      <Box sx={{ flex: 1, pt: 0.75, pb: isLast ? 0 : 1.5 }}>
        <Typography
          variant="body2"
          fontWeight={step.current ? 700 : 600}
          color={step.reached ? "text.primary" : "text.disabled"}
        >
          {step.label}
        </Typography>
        {step.reached && step.date && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.35 }}>
            {formatDate(step.date)}
          </Typography>
        )}
        {step.reached && step.by && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 0.25, fontStyle: "italic" }}
          >
            {formatChangedBy(step.by)}
          </Typography>
        )}
        {step.reached && <StepRemark remark={step.remark} align="left" />}
      </Box>
    </Stack>
  );
}

function VerticalPrepTimelineStep({
  step,
  showConnector,
}: {
  step: TimelineStep;
  showConnector: boolean;
}) {
  const theme = useTheme();
  const activeColor = stepAccentColor(step, theme);

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ width: "100%", minHeight: 56, pl: 3 }}
    >
      <Stack alignItems="center" sx={{ width: 36, flexShrink: 0 }}>
        <Box
          sx={{
            width: 2,
            height: 10,
            bgcolor: alpha(theme.palette.grey[400], 0.35),
            borderRadius: 1,
          }}
        />
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: step.reached ? "#fff" : theme.palette.text.disabled,
            bgcolor: step.reached ? activeColor : alpha(theme.palette.grey[300], 0.35),
            border: `2px solid ${activeColor}`,
          }}
        >
          {step.icon}
        </Box>
        {showConnector && (
          <Box
            sx={{
              flex: 1,
              width: 2,
              minHeight: 12,
              mt: 0.5,
              bgcolor: step.reached
                ? alpha(theme.palette.success.main, 0.45)
                : alpha(theme.palette.grey[400], 0.35),
              borderRadius: 1,
            }}
          />
        )}
      </Stack>
      <Box sx={{ flex: 1, pt: 1.25, pb: showConnector ? 1 : 0 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          color={step.reached ? "text.primary" : "text.disabled"}
          sx={{ fontSize: "0.85rem" }}
        >
          {step.label}
        </Typography>
        {step.reached && step.date && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
            {formatDate(step.date)}
          </Typography>
        )}
        {step.reached && step.by && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 0.25, fontStyle: "italic" }}
          >
            {formatChangedBy(step.by)}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

function TimelineArrow({ vertical }: { vertical: boolean }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "text.disabled",
        px: vertical ? 0 : 0.5,
        py: vertical ? 0.25 : 0,
        transform: vertical ? "rotate(90deg)" : "none",
      }}
    >
      <ChevronRight fontSize="small" />
    </Box>
  );
}

function EmployeeHeader({
  employee,
  statusLabel,
  showCurrentRemark = true,
}: {
  employee: Employee;
  statusLabel: string;
  showCurrentRemark?: boolean;
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const currentRemark = getEmployeeStatusRemark(employee)?.trim();

  const displayName = [employee.givenName, employee.surName]
    .filter(Boolean)
    .join(" ");

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 2.5 },
        py: { xs: 1.75, sm: 2 },
        borderRadius: 2,
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.primary.main,
          0.1,
        )}, ${alpha(theme.palette.secondary.main, 0.08)})`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {displayName || t("candidate")}
          </Typography>
          <Stack
            direction="row"
            spacing={0.75}
            flexWrap="wrap"
            sx={{ mt: 0.75, gap: 0.75 }}
          >
            {employee.referenceNumber != null && (
              <Chip
                size="small"
                label={`#${employee.referenceNumber}`}
                sx={{ height: 24, fontSize: "0.72rem" }}
              />
            )}
            {employee.passportNumber && (
              <Chip
                size="small"
                variant="outlined"
                label={`${t("passportNumber")}: ${employee.passportNumber}`}
                sx={{ height: 24, fontSize: "0.72rem" }}
              />
            )}
          </Stack>
        </Box>
        <Chip
          label={statusLabel}
          color="primary"
          size="small"
          sx={{ fontWeight: 600, flexShrink: 0 }}
        />
      </Stack>
      {showCurrentRemark && currentRemark && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1.5, lineHeight: 1.45 }}
        >
          <Box component="span" fontWeight={600} color="text.primary">
            {t("currentStatusRemark")}:
          </Box>{" "}
          {currentRemark}
        </Typography>
      )}
    </Box>
  );
}

export function EmployeeStatusHistoryDialog({ employee, loading }: Props) {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");

  const timelineItems = useMemo(() => {
    if (!employee) return [];
    return buildTimelineItems(
      employee,
      (status) => te(status),
      t("applicationDate"),
      te("medical"),
      te("coc"),
    );
  }, [employee, te, t]);

  const progressSteps = useMemo(
    () => flattenStepsForProgress(timelineItems),
    [timelineItems],
  );

  const verticalRows = useMemo(
    () => flattenVerticalRows(timelineItems),
    [timelineItems],
  );

  const terminalStep = useMemo(() => {
    if (!employee) return null;
    return buildTerminalStep(employee, (status) => te(status));
  }, [employee, te]);

  const reachedCount = progressSteps.filter((step) => step.reached).length;
  const progressPct =
    progressSteps.length > 0
      ? Math.round((reachedCount / progressSteps.length) * 100)
      : 0;

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: 3,
          py: 8,
        }}
      >
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  if (!employee) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ px: 3, py: 4, textAlign: "center" }}
      >
        {t("no_status_history_yet")}
      </Typography>
    );
  }

  if (reachedCount === 0 && !terminalStep) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ px: 3, py: 4, textAlign: "center" }}
      >
        {t("no_status_history_yet")}
      </Typography>
    );
  }

  const currentStatusLabel = te(employee.status ?? EmployeeStatus.new);

  return (
    <Stack
      spacing={2.5}
      sx={{
        px: { xs: 2, sm: 3 },
        pt: { xs: 2, sm: 2.5 },
        pb: { xs: 3, sm: 3.5 },
      }}
    >
      <EmployeeHeader
        employee={employee}
        statusLabel={currentStatusLabel}
        showCurrentRemark={!terminalStep}
      />

      {!terminalStep && progressSteps.length > 0 && (
        <Box sx={{ px: 0.5 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 0.75 }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {t("status")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {reachedCount} / {progressSteps.length}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={progressPct}
            color="success"
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.success.main, 0.12),
            }}
          />
        </Box>
      )}

      <Box
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 2, sm: 2.5 },
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          bgcolor: alpha(theme.palette.background.default, 0.55),
          overflowX: isNarrow ? "visible" : "auto",
        }}
      >
        {isNarrow ? (
          <Stack spacing={0} sx={{ width: "100%" }}>
            {verticalRows.map((row) =>
              row.type === "main" ? (
                <VerticalTimelineStep
                  key={row.step.id}
                  step={row.step}
                  isLast={row.isLast}
                />
              ) : (
                <VerticalPrepTimelineStep
                  key={row.step.id}
                  step={row.step}
                  showConnector={row.showConnector}
                />
              ),
            )}
          </Stack>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              justifyContent: "center",
              gap: 0.25,
              minWidth: 860,
              py: 0.5,
            }}
          >
            {timelineItems.map((item, index) => {
              const nextItem = timelineItems[index + 1];
              const showArrow =
                index < timelineItems.length - 1 &&
                (item.kind === "stepWithPrep"
                  ? item.step.reached ||
                    item.prepSteps.some((prep) => prep.reached) ||
                    (nextItem?.kind === "step" && nextItem.step.reached) ||
                    (nextItem?.kind === "stepWithPrep" && nextItem.step.reached)
                  : item.step.reached ||
                    (nextItem?.kind === "step" && nextItem.step.reached) ||
                    (nextItem?.kind === "stepWithPrep" &&
                      (nextItem.step.reached ||
                        nextItem.prepSteps.some((prep) => prep.reached))));

              return (
                <Box
                  key={item.kind === "stepWithPrep" ? item.step.id : item.step.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  {item.kind === "stepWithPrep" ? (
                    <>
                      <TimelineNode step={item.step} vertical={false} />
                      {item.prepSteps.map((prepStep) => (
                        <PrepTimelineNode key={prepStep.id} step={prepStep} />
                      ))}
                    </>
                  ) : (
                    <TimelineNode step={item.step} vertical={false} />
                  )}
                  {showArrow && <TimelineArrow vertical={false} />}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {terminalStep && (
        <Box
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: { xs: 1.75, sm: 2 },
            borderRadius: 2,
            border: `1px dashed ${theme.palette.warning.main}`,
            bgcolor: alpha(theme.palette.warning.main, 0.08),
          }}
        >
          <Typography
            variant="overline"
            color="text.secondary"
            display="block"
            sx={{ lineHeight: 1.4, letterSpacing: 0.6 }}
          >
            {t("status")}
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 0.5, sm: 1.5 }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{ mt: 0.5 }}
          >
            <Typography variant="body2" fontWeight={700}>
              {terminalStep.label}
            </Typography>
            {terminalStep.date && (
              <>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ display: { xs: "none", sm: "block" } }}
                />
                <Typography variant="body2" color="text.secondary">
                  {formatDate(terminalStep.date)}
                </Typography>
              </>
            )}
            {terminalStep.by && (
              <>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ display: { xs: "none", sm: "block" } }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: "italic" }}
                >
                  {formatChangedBy(terminalStep.by)}
                </Typography>
              </>
            )}
          </Stack>
          {terminalStep.remark && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, lineHeight: 1.45, fontStyle: "italic" }}
            >
              {t("remark")}: {terminalStep.remark}
            </Typography>
          )}
        </Box>
      )}
    </Stack>
  );
}
